"use client";

import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Phone, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-1">
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted-light animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild className="group relative px-4 py-2 text-sm font-medium text-foreground-dark hover:text-accent transition-colors duration-[var(--duration-medium)]">
              <Link to={item.href || "#"}>
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-0.5 w-0 bg-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:w-full group-hover:left-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
        <NavigationMenuItem key="static-reservation" data-index="static-reservation">
          <NavigationMenuLink asChild className="group relative px-4 py-2 text-sm font-medium text-foreground-dark hover:text-accent transition-colors duration-[var(--duration-medium)]">
            <Link to="https://book.mylimobiz.com/v4/CSC">
              Make A Reservation
              <span className="absolute bottom-0 left-1/2 h-0.5 w-0 bg-accent transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] group-hover:w-full group-hover:left-0" />
            </Link>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2 p-4">
        <div className="h-12 w-full bg-muted-light animate-pulse rounded-md" />
        <div className="h-12 w-full bg-muted-light animate-pulse rounded-md" />
        <div className="h-12 w-full bg-muted-light animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} onClick={onItemClick} className="px-4 py-3 text-base font-medium text-foreground-light hover:bg-accent/20 hover:text-accent rounded-md transition-colors duration-[var(--duration-medium)]">
          {item.label}
        </Link>)}
      <Link key="static-reservation" data-index="static-reservation" to="https://book.mylimobiz.com/v4/CSC" onClick={onItemClick} className="px-4 py-3 text-base font-medium text-foreground-light hover:bg-accent/20 hover:text-accent rounded-md transition-colors duration-[var(--duration-medium)]">
        Make A Reservation
      </Link>
    </nav>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 bg-background transition-shadow duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${scrolled ? "shadow-medium" : "shadow-soft"}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {}
          <div className="flex-shrink-0">
            <Link to="/">
              <img src="https://present-gnat.10web.cloud/wp-content/uploads/2025/11/cropped-ChatGPT-Image-Oct-18-2025-01_19_50-AM.png" alt="Car Service Connecticut" className="h-12" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="30">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-2">
            <Badge asChild variant="outline" className="hover-lift cursor-pointer border-primary/20 bg-primary-light/50 text-foreground-dark hover:bg-primary-light hover:border-primary/40 transition-all duration-[var(--duration-medium)]">
              <Link to="tel:(203) 872-9965" className="flex items-center gap-2">
                <Phone className="size-3" />
                <span className="hidden sm:inline font-semibold">(203) 872-9965</span>
              </Link>
            </Badge>

            {}
            <Button onClick={toggleMobileMenu} aria-label="Toggle mobile menu" variant="ghost" size="icon" className="md:hidden text-foreground-dark hover:text-accent transition-colors duration-[var(--duration-medium)]">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <>
          {}
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 md:hidden animate-fade-in-up" onClick={closeMobileMenu} />
          
          {}
          <div className="fixed top-16 left-0 right-0 bg-secondary border-t border-muted/20 shadow-strong z-50 md:hidden animate-fade-in-up overflow-y-auto max-h-[calc(100vh-4rem)]">
            <MenuProvider menu_id="30">
              <MobileMenuConsumer onItemClick={closeMobileMenu} />
            </MenuProvider>
          </div>
        </>}
    </header>;
}